import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { API_BASE_URL } from './api-base';

export type ControlcliReference = 'CLIENT' | 'PROSPECT' | 'COMMENT' | 'PROPOSAL';

export interface ControlcliClient {
  id: number;
  tenant_id: number;
  cnpj: string;
  name: string;
  website?: string | null;
  segment?: string | null;
  description?: string | null;
  active: number;
  created_by: number;
  created_at: string;
}

export interface ControlcliContact {
  id: number;
  tenant_id: number;
  client_id: number;
  name: string;
  area?: string | null;
  role?: string | null;
  email: string;
  phone?: string | null;
  created_by: number;
  created_at: string;
}

export interface ControlcliServiceItem {
  id: number;
  tenant_id: number;
  name: string;
  description?: string | null;
  product_link?: string | null;
  presentation_path?: string | null;
  service_type: 'SERVICE' | 'PRODUCT' | 'SUBSCRIPTION' | 'PROJECT';
  active: number;
  created_by: number;
  created_at: string;
}

export interface ControlcliProspect {
  id: number;
  tenant_id: number;
  client_id?: number | null;
  service_id: number;
  prospect_name: string;
  need_description: string;
  status: 'NEW' | 'CONTACTED' | 'PROPOSAL_SENT' | 'NEGOTIATION' | 'SIGNATURE' | 'WON' | 'LOST';
  responsible_user_id: number;
  created_at: string;
  client_name?: string;
  service_name?: string;
  responsible_name?: string;
}

export interface ControlcliProposal {
  id: number;
  tenant_id: number;
  prospect_id: number;
  proposal_code: string;
  description: string;
  value_total: number;
  value_monthly?: number | null;
  value_annual?: number | null;
  proposal_version?: number | null;
  proposal_year?: number | null;
  proposal_seq?: number | null;
  valid_until: string;
  status: 'SENT' | 'ACCEPTED' | 'REJECTED' | 'EXPIRED';
  sent_at: string;
  sent_by: number;
  prospect_name?: string;
  attachments?: ControlcliAttachment[];
}

export interface ControlcliComment {
  id: number;
  tenant_id: number;
  reference_type: 'CLIENT' | 'PROSPECT';
  reference_id: number;
  user_id: number;
  comment: string;
  created_at: string;
  author_name?: string;
  attachments?: ControlcliAttachment[];
}

export interface ControlcliAttachment {
  id: number;
  tenant_id: number;
  reference_type: ControlcliReference;
  reference_id: number;
  file_name: string;
  file_path: string;
  file_type: string;
  uploaded_by: number;
  uploaded_at: string;
}

export interface ControlcliSurvey {
  id: number;
  tenant_id: number;
  title: string;
  description?: string | null;
  active: number;
  created_by: number;
  created_at: string;
}

export interface ControlcliSurveyResponse {
  id: number;
  tenant_id: number;
  survey_id: number;
  client_id: number;
  score: number;
  feedback?: string | null;
  responded_at: string;
  survey_title?: string;
  client_name?: string;
}

@Injectable({ providedIn: 'root' })
export class ControlcliService {
  private readonly baseUrl = `${API_BASE_URL}/controlcli`;

  constructor(private http: HttpClient) {}

  // Clientes
  getClients(tenantId: number, search = ''): Observable<ControlcliClient[]> {
    let params = new HttpParams().set('tenant_id', tenantId);
    if (search) {
      params = params.set('search', search);
    }
    return this.http.get<ControlcliClient[]>(`${this.baseUrl}/clients`, { params });
  }

  createClient(payload: any) {
    return this.http.post(`${this.baseUrl}/clients`, payload);
  }

  updateClient(id: number, payload: any) {
    return this.http.put(`${this.baseUrl}/clients/${id}`, payload);
  }

  // Contatos
  createContact(clientId: number, payload: any) {
    return this.http.post(`${this.baseUrl}/clients/${clientId}/contacts`, payload);
  }

  getContacts(tenantId: number) {
    const params = new HttpParams().set('tenant_id', tenantId);
    return this.http.get<any[]>(`${this.baseUrl}/contacts`, { params });
  }

  updateContact(contactId: number, payload: any) {
    return this.http.put(`${this.baseUrl}/contacts/${contactId}`, payload);
  }

  // Serviços
  getServices(tenantId: number): Observable<ControlcliServiceItem[]> {
    const params = new HttpParams().set('tenant_id', tenantId);
    return this.http.get<ControlcliServiceItem[]>(`${this.baseUrl}/services`, { params });
  }

  createService(payload: any) {
    return this.http.post(`${this.baseUrl}/services`, payload);
  }

  updateService(id: number, payload: any) {
    return this.http.put(`${this.baseUrl}/services/${id}`, payload);
  }

  // Prospects
  getProspects(tenantId: number, status?: string): Observable<ControlcliProspect[]> {
    let params = new HttpParams().set('tenant_id', tenantId);
    if (status) {
      params = params.set('status', status);
    }
    return this.http.get<ControlcliProspect[]>(`${this.baseUrl}/prospects`, { params });
  }

  createProspect(payload: any) {
    return this.http.post(`${this.baseUrl}/prospects`, payload);
  }

  updateProspect(id: number, payload: any) {
    return this.http.put(`${this.baseUrl}/prospects/${id}`, payload);
  }

  // Propostas
  getProposals(tenantId: number): Observable<ControlcliProposal[]> {
    const params = new HttpParams().set('tenant_id', tenantId);
    return this.http.get<ControlcliProposal[]>(`${this.baseUrl}/proposals`, { params });
  }

  createProposal(payload: any) {
    return this.http.post(`${this.baseUrl}/proposals`, payload);
  }

  getNextProposalCode(tenantId: number) {
    const params = new HttpParams().set('tenant_id', tenantId);
    return this.http.get<{ proposal_code: string; proposal_year: number; proposal_seq: number }>(
      `${this.baseUrl}/proposals/next-code`,
      { params }
    );
  }

  updateProposal(id: number, payload: any) {
    return this.http.put(`${this.baseUrl}/proposals/${id}`, payload);
  }

  uploadProposalAttachment(proposalId: number, formData: FormData) {
    return this.http.post(`${this.baseUrl}/proposals/${proposalId}/attachments`, formData);
  }

  // Comentários
  addComment(payload: any) {
    return this.http.post(`${this.baseUrl}/comments`, payload);
  }

  getComments(tenantId: number, reference_type: 'CLIENT' | 'PROSPECT', reference_id: number) {
    const params = new HttpParams()
      .set('tenant_id', tenantId)
      .set('reference_type', reference_type)
      .set('reference_id', reference_id);
    return this.http.get<ControlcliComment[]>(`${this.baseUrl}/comments`, { params });
  }

  updateComment(id: number, payload: any) {
    return this.http.put(`${this.baseUrl}/comments/${id}`, payload);
  }

  uploadCommentAttachment(commentId: number, formData: FormData) {
    return this.http.post(`${this.baseUrl}/comments/${commentId}/attachments`, formData);
  }

  // Pesquisas de satisfação
  getSurveys(tenantId: number): Observable<ControlcliSurvey[]> {
    const params = new HttpParams().set('tenant_id', tenantId);
    return this.http.get<ControlcliSurvey[]>(`${this.baseUrl}/surveys`, { params });
  }

  createSurvey(payload: any) {
    return this.http.post(`${this.baseUrl}/surveys`, payload);
  }

  updateSurvey(id: number, payload: any) {
    return this.http.put(`${this.baseUrl}/surveys/${id}`, payload);
  }

  getSurveyResponses(tenantId: number): Observable<ControlcliSurveyResponse[]> {
    const params = new HttpParams().set('tenant_id', tenantId);
    return this.http.get<ControlcliSurveyResponse[]>(`${this.baseUrl}/survey-responses`, { params });
  }
}
